import React, { useRef, useState } from 'react';
import { ProTable } from '@ant-design/pro-components';
import { Button, Tag, Input, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import type { ProColumns, ActionType } from '@ant-design/pro-components';
import { getUsers, AdminUser } from '../../api/admin';

const roleMap: Record<string, { color: string; text: string }> = {
  super_admin: { color: 'red', text: '超级管理员' },
  admin: { color: 'blue', text: '管理员' },
  user: { color: 'default', text: '普通用户' },
};

const statusMap: Record<string, { color: string; text: string }> = {
  active: { color: 'green', text: '正常' },
  disabled: { color: 'orange', text: '禁用' },
  banned: { color: 'red', text: '封禁' },
};

const Users: React.FC = () => {
  const actionRef = useRef<ActionType>();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');

  const columns: ProColumns<AdminUser>[] = [
    { title: '昵称', dataIndex: 'nickname', ellipsis: true, width: 140 },
    { title: '邮箱', dataIndex: 'email', copyable: true, width: 200 },
    { title: '手机号', dataIndex: 'phone', copyable: true, width: 130 },
    {
      title: '角色',
      dataIndex: 'role',
      width: 100,
      render: (_, record) => {
        const r = roleMap[record.role] || { color: 'default', text: record.role };
        return <Tag color={r.color}>{r.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (_, record) => {
        const s = statusMap[record.status] || { color: 'default', text: record.status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      width: 180,
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      valueType: 'dateTime',
      width: 180,
    },
    {
      title: '上一次会话',
      dataIndex: 'lastSessionAt',
      width: 180,
      render: (_, record) => (record.lastSessionAt ? new Date(Number(record.lastSessionAt) * 1000).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/users/${record.id}`)}>
          查看
        </Button>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ProTable<AdminUser>
        actionRef={actionRef}
        columns={columns}
        request={async (params) => {
          const res = await getUsers({
            keyword: params.keyword as string,
            page: params.current,
            pageSize: params.pageSize,
          });
          return {
            data: res.items,
            success: true,
            total: res.total,
          };
        }}
        rowKey="id"
        pagination={{ pageSize: 10, showSizeChanger: true }}
        search={false}
        scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
        toolbar={{
          search: (
            <Space>
              <Input
                placeholder="搜索昵称、邮箱、手机号"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={() => actionRef.current?.reload()}
                style={{ width: 280 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => actionRef.current?.reload()}
              >
                搜索
              </Button>
            </Space>
          ),
          settings: [],
        }}
      />
    </div>
  );
};

export default Users;
